import type { Tower } from "@/types/tower.types"
import type { Position, ShrineAuraState } from "@/types/game.types"
import type { UnitShrineBuffs } from "@/types/hero.types"

/**
 * Get all active shrine auras from towers
 */
export function getActiveShrineAuras(towers: Tower[]): ShrineAuraState[] {
  return towers
    .filter((tower) => tower.type === "shrine")
    .map((shrine) => ({
      shrineId: shrine.id,
      shrineLevel: shrine.level,
      position: shrine.position,
      radius: getShrineEffectRadius(shrine.level),
    }))
}

/**
 * Calculate shrine effect radius - scales with shrine level
 * Shrine radii: [150, 175, 200, 225, 250, 275, 300]
 * Base radius 150px, +25px per level
 */
export function getShrineEffectRadius(shrineLevel: number): number {
  const baseRadius = 150
  const radiusPerLevel = 25
  return baseRadius + (shrineLevel - 1) * radiusPerLevel
}

/**
 * Get strongest shrine affecting a position
 * Returns the shrine with the highest level (largest radius)
 * Multiple shrines don't stack - only the strongest applies
 */
export function getStrongestShrineAtPosition(
  position: Position,
  shrineAuras: ShrineAuraState[],
): ShrineAuraState | null {
  let strongest: ShrineAuraState | null = null

  for (const shrine of shrineAuras) {
    const dx = position.x - shrine.position.x
    const dy = position.y - shrine.position.y
    const distSq = dx * dx + dy * dy
    const radiusSq = shrine.radius * shrine.radius

    if (distSq <= radiusSq) {
      if (!strongest || shrine.shrineLevel > strongest.shrineLevel) {
        strongest = shrine
      }
    }
  }

  return strongest
}

/**
 * Calculate all buffs from heroism level
 * Buffs per level:
 * - Movement Speed: +7.5% (0.075)
 * - XP Gain: +5% (0.05) - Heroes only
 * - Max Health: +5% (0.05)
 * - Base Damage: +3% (0.03)
 * - Armor: +1 (flat)
 */
export function calculateShrineBuffs(heroismLevel: number): UnitShrineBuffs {
  return {
    movementSpeedBonus: heroismLevel * 0.075, // +7.5% per level
    xpGainBonus: heroismLevel * 0.05, // +5% per level
    healthBonus: heroismLevel * 0.05, // +5% per level
    damageBonus: heroismLevel * 0.03, // +3% per level
    armorBonus: heroismLevel, // +1 per level (flat)
    activeAuraShrineId: null, // Set by caller
  }
}

/**
 * Get heroism cost for next level
 * Formula: 100 * (1.75 ^ level)
 * Costs: [100, 175, 306, 536, 938, 1641, 2871]
 */
export function getHeroismCost(currentLevel: number): number {
  if (currentLevel >= 7) return 0 // Max level
  return Math.floor(100 * Math.pow(1.75, currentLevel))
}

/**
 * Get the gold cost to buy the next level of a hero skill at the shrine.
 * Mirrors the heroism curve but on a gentler slope, since skills also level up
 * through skill points as the hero gains levels.
 * Costs from level 0: [75, 124, 205, 338, 557, 919, 1516, 2501, 4127, 6810]
 */
export function getShrineSkillUpgradeCost(currentSkillLevel: number): number {
  return Math.floor(75 * Math.pow(1.65, currentSkillLevel))
}

/**
 * Get empty shrine buffs (when not in aura)
 */
export function getEmptyShrineBuffs(): UnitShrineBuffs {
  return {
    movementSpeedBonus: 0,
    xpGainBonus: 0,
    healthBonus: 0,
    damageBonus: 0,
    armorBonus: 0,
    activeAuraShrineId: null,
  }
}
